import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Send, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "@/i18n/useTranslation";

const ease = [0.16, 1, 0.3, 1] as const;

const ContactSection = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    try {
      const { data, error } = await supabase.functions.invoke("send-contact", {
        body: {
          name: formData.get("name"),
          company: formData.get("company"),
          email: formData.get("email"),
          message: formData.get("message"),
          website: formData.get("website"),
        },
      });

      if (error) throw error;

      toast({
        title: t.contact.successTitle,
        description: t.contact.successDesc,
      });
      form.reset();
    } catch (err) {
      console.error("Contact form error:", err);
      toast({
        title: t.contact.errorTitle,
        description: t.contact.errorDesc,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease }}
          >
            <div className="text-center mb-10">
              <p className="label-text mb-3">{t.contact.label}</p>
              <h2 className="font-display font-bold text-foreground mb-4">
                {t.contact.heading}
              </h2>
              <p className="text-muted-foreground font-body">
                {t.contact.subheading}
              </p>
            </div>

            <motion.form
              onSubmit={handleSubmit}
              className="space-y-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2, ease }}
            >
              <div className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10" aria-hidden="true">
                <Input name="website" tabIndex={-1} autoComplete="off" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block font-body">
                    {t.contact.nameLabel}
                  </label>
                  <Input required name="name" placeholder={t.contact.namePlaceholder} />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block font-body">
                    {t.contact.companyLabel}
                  </label>
                  <Input name="company" placeholder={t.contact.companyPlaceholder} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block font-body">
                  {t.contact.emailLabel}
                </label>
                <Input required name="email" type="email" placeholder={t.contact.emailPlaceholder} />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block font-body">
                  {t.contact.messageLabel}
                </label>
                <Textarea
                  required
                  name="message"
                  rows={5}
                  placeholder={t.contact.messagePlaceholder}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                size="lg"
                variant="premium"
                disabled={loading}
              >
                {loading ? t.contact.submitting : t.contact.submit}
                <Send size={16} />
              </Button>
            </motion.form>

            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <a
                href="mailto:jw@eastwestit.dk"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors font-body"
              >
                <Mail size={16} />
                {t.contact.emailDirect}
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
